import { useCallback, useState } from 'react';

import { Contact, Subscription, TagSchemaField, Topic } from '@/core/notifications/model';
import {
  invalidTagValues as computeInvalidTagValues,
  missingRequiredTags as computeMissingRequiredTags,
  toValueList,
} from '@/core/notifications/model/tag-filter-utils';

// 'topic' and 'delivery' are fixed; one 'scope:<tagKey>' step per tagSchema
// field, plus a final 'scope-confirm' review step, are computed at render
// time from the selected topic (see scopeStepsFor in subscription-wizard-body.tsx)
// — a topic's field set isn't known until it's selected, so this can't be a
// closed string union the way the old 3-step wizard's was.
export type WizardStep = 'topic' | 'scope-confirm' | 'delivery' | `scope:${string}`;

export function scopeFieldStep(tagKey: string): WizardStep {
  return `scope:${tagKey}`;
}

export function tagKeyOfScopeStep(step: WizardStep): string | undefined {
  return step.startsWith('scope:') ? step.slice('scope:'.length) : undefined;
}

// What a module/pipeline hands the wizard when deep-linking into subscription
// creation with a topic (and optionally a filter) already decided.
export interface WizardPreload {
  topicKey: string;
  tagFilter?: Record<string, unknown>;
  suggestedName?: string;
}

export type ScopeMode = 'all' | 'narrow';

export interface WizardState {
  step: WizardStep;
  unlockedSteps: Set<WizardStep>;
  // Set while a single field's step was entered via an "Edit" link from a
  // summary (scope-confirm, or the preload flow's fixed delivery-step
  // summary) — see goToFieldForEdit/finishEditingField below.
  editReturnStep: WizardStep | null;
  applicationId?: string;
  topicKey: string;
  name: string;
  scopeModeByField: Record<string, ScopeMode>;
  tagValues: Record<string, string[]>;
  expandedFieldKeys: Set<string>;
  selectedChannels: string[];
  overrides: Record<string, Record<string, unknown>>;
  contacts: Contact[];
  saving: boolean;
  errorMessage: string | null;
}

function tagValuesFromFilter(
  tagFilter: Record<string, unknown> | undefined,
): Record<string, string[]> {
  const values: Record<string, string[]> = {};
  Object.entries(tagFilter ?? {}).forEach(([key, value]) => {
    values[key] = toValueList(value);
  });
  return values;
}

// Auto-expand required fields (Step 2 UX decision) whenever a topic is mounted,
// whether that's the initial topic (existing/preloaded/default) or a re-selection.
function requiredFieldKeys(topic: Topic | undefined): Set<string> {
  if (!topic) return new Set();
  return new Set(
    Object.entries(topic.tagSchema)
      .filter(([, field]) => field.required)
      .map(([key]) => key),
  );
}

// A required field can never be satisfied by "all" (an empty filter), so
// defaulting to it just leaves that field's Next disabled with no value
// picker visible and no obvious way forward — narrow it from the start.
// A field that already has values (edit mode, or a preload that supplied
// them) starts narrowed too, regardless of required-ness, so the user's
// existing choice isn't silently discarded on open.
function scopeModeByFieldFor(
  tagSchema: Record<string, TagSchemaField>,
  tagValues: Record<string, string[]>,
): Record<string, ScopeMode> {
  const modes: Record<string, ScopeMode> = {};
  Object.entries(tagSchema).forEach(([key, field]) => {
    const hasValues = (tagValues[key] ?? []).length > 0;
    modes[key] = hasValues || field.required ? 'narrow' : 'all';
  });
  return modes;
}

function buildInitialState(
  topics: Topic[],
  existingSubscription?: Subscription | null,
  preload?: WizardPreload | null,
): WizardState {
  if (existingSubscription) {
    const topic = topics.find(t => t.key === existingSubscription.topic);
    const tagValues = tagValuesFromFilter(existingSubscription.tagFilter);
    const defaultChannels = existingSubscription.defaultChannels ?? [];
    return {
      step: 'topic',
      // topic is immutable in edit mode, but every step is still reachable —
      // the topic step just renders its selector disabled.
      unlockedSteps: new Set(['topic', 'scope-confirm']),
      editReturnStep: null,
      applicationId: topic?.applicationId,
      topicKey: existingSubscription.topic,
      name: existingSubscription.name,
      scopeModeByField: scopeModeByFieldFor(topic?.tagSchema ?? {}, tagValues),
      tagValues,
      expandedFieldKeys: requiredFieldKeys(topic),
      selectedChannels: defaultChannels.includes('in-app')
        ? defaultChannels
        : ['in-app', ...defaultChannels],
      overrides: existingSubscription.channelOverrides ?? {},
      contacts: [],
      saving: false,
      errorMessage: null,
    };
  }

  if (preload) {
    const topic = topics.find(t => t.key === preload.topicKey);
    const tagValues = tagValuesFromFilter(preload.tagFilter);
    return {
      step: 'delivery',
      // Topic is fixed forever in preload mode (the calling module decided
      // it, not something "modify the filter" should ever touch) — it's
      // deliberately never added here even once scope steps unlock.
      unlockedSteps: new Set(),
      editReturnStep: null,
      applicationId: topic?.applicationId,
      topicKey: preload.topicKey,
      name: preload.suggestedName ?? '',
      scopeModeByField: scopeModeByFieldFor(topic?.tagSchema ?? {}, tagValues),
      tagValues,
      expandedFieldKeys: requiredFieldKeys(topic),
      selectedChannels: ['in-app'],
      overrides: {},
      contacts: [],
      saving: false,
      errorMessage: null,
    };
  }

  const defaultTopic = topics[0];
  return {
    step: 'topic',
    unlockedSteps: new Set(),
    editReturnStep: null,
    applicationId: defaultTopic?.applicationId,
    topicKey: defaultTopic?.key ?? '',
    name: '',
    scopeModeByField: scopeModeByFieldFor(defaultTopic?.tagSchema ?? {}, {}),
    tagValues: {},
    expandedFieldKeys: requiredFieldKeys(defaultTopic),
    selectedChannels: ['in-app'],
    overrides: {},
    contacts: [],
    saving: false,
    errorMessage: null,
  };
}

export default function useSubscriptionWizardState({
  topics,
  existingSubscription,
  preload,
}: {
  topics: Topic[];
  existingSubscription?: Subscription | null;
  preload?: WizardPreload | null;
}) {
  const [state, setState] = useState<WizardState>(() =>
    buildInitialState(topics, existingSubscription, preload),
  );

  const isEdit = !!existingSubscription;
  const selectedTopic = topics.find(topic => topic.key === state.topicKey);
  const tagSchema = selectedTopic?.tagSchema ?? {};

  const setStep = useCallback((step: WizardStep) => {
    setState(prev => ({ ...prev, step }));
  }, []);

  // Re-selecting a topic drops any filter values from the previous topic (they
  // don't apply to the new tagSchema) and reseeds the auto-expand set.
  const selectTopic = useCallback(
    (topicKey: string) => {
      const topic = topics.find(t => t.key === topicKey);
      setState(prev => ({
        ...prev,
        topicKey,
        applicationId: topic?.applicationId,
        tagValues: {},
        scopeModeByField: scopeModeByFieldFor(topic?.tagSchema ?? {}, {}),
        expandedFieldKeys: requiredFieldKeys(topic),
      }));
    },
    [topics],
  );

  const setName = useCallback((name: string) => {
    setState(prev => ({ ...prev, name }));
  }, []);

  const setScopeModeForField = useCallback((tagKey: string, mode: ScopeMode) => {
    setState(prev => ({
      ...prev,
      scopeModeByField: { ...prev.scopeModeByField, [tagKey]: mode },
    }));
  }, []);

  const toggleExpanded = useCallback((fieldKey: string) => {
    setState(prev => {
      const expandedFieldKeys = new Set(prev.expandedFieldKeys);
      if (expandedFieldKeys.has(fieldKey)) expandedFieldKeys.delete(fieldKey);
      else expandedFieldKeys.add(fieldKey);
      return { ...prev, expandedFieldKeys };
    });
  }, []);

  const toggleTagLeaf = useCallback((tagKey: string, value: string) => {
    setState(prev => {
      const current = prev.tagValues[tagKey] ?? [];
      const next = current.includes(value)
        ? current.filter(existing => existing !== value)
        : [...current, value];
      return { ...prev, tagValues: { ...prev.tagValues, [tagKey]: next } };
    });
  }, []);

  // group checkboxes only ever add/remove their descendant leaves; groups never enter tagValues
  const setTagGroupLeaves = useCallback((tagKey: string, leafKeys: string[], checked: boolean) => {
    setState(prev => {
      const cleared = (prev.tagValues[tagKey] ?? []).filter(key => !leafKeys.includes(key));
      return {
        ...prev,
        tagValues: { ...prev.tagValues, [tagKey]: checked ? [...cleared, ...leafKeys] : cleared },
      };
    });
  }, []);

  const setTagFreeText = useCallback((tagKey: string, raw: string) => {
    setState(prev => ({
      ...prev,
      tagValues: {
        ...prev.tagValues,
        [tagKey]: raw
          .split(',')
          .map(part => part.trim())
          .filter(part => part.length > 0),
      },
    }));
  }, []);

  const toggleChannel = useCallback((channelKey: string) => {
    setState(prev => ({
      ...prev,
      selectedChannels: prev.selectedChannels.includes(channelKey)
        ? prev.selectedChannels.filter(existing => existing !== channelKey)
        : [...prev.selectedChannels, channelKey],
    }));
  }, []);

  const setOverride = useCallback((channelKey: string, config: Record<string, unknown>) => {
    setState(prev => ({ ...prev, overrides: { ...prev.overrides, [channelKey]: config } }));
  }, []);

  const setContacts = useCallback((contacts: Contact[]) => {
    setState(prev => ({ ...prev, contacts }));
  }, []);

  const setSaving = useCallback((saving: boolean) => {
    setState(prev => ({ ...prev, saving }));
  }, []);

  const setErrorMessage = useCallback((errorMessage: string | null) => {
    setState(prev => ({ ...prev, errorMessage }));
  }, []);

  // Jumps straight to one field's own step from a summary (scope-confirm, or
  // the preload flow's fixed delivery-step summary) — unlocking only that one
  // field, never the rest of the schema and never 'scope-confirm'/'topic', so
  // reachableSteps ends up containing just [that field, ...whatever was
  // already reachable], and finishing the edit returns straight to
  // `returnStep` instead of stepping through every other field first.
  const goToFieldForEdit = useCallback((tagKey: string, returnStep: WizardStep) => {
    setState(prev => {
      const step = scopeFieldStep(tagKey);
      const unlockedSteps = new Set(prev.unlockedSteps);
      unlockedSteps.add(step);
      return { ...prev, unlockedSteps, step, editReturnStep: returnStep };
    });
  }, []);

  // The counterpart to goToFieldForEdit: jumps back to the summary that was
  // being edited from, and clears the "currently editing one field" flag.
  const finishEditingField = useCallback(() => {
    setState(prev =>
      prev.editReturnStep ? { ...prev, step: prev.editReturnStep, editReturnStep: null } : prev,
    );
  }, []);

  // Effective values feed both validation and the save payload: "all" for a
  // field means empty, whatever was previously typed into it, so switching
  // back to "all" after narrowing never leaks a stale selection.
  const effectiveTagValues: Record<string, string[]> = {};
  Object.keys(tagSchema).forEach(key => {
    effectiveTagValues[key] =
      (state.scopeModeByField[key] ?? 'all') === 'narrow' ? (state.tagValues[key] ?? []) : [];
  });
  const missingRequiredTagKeys = computeMissingRequiredTags(tagSchema, effectiveTagValues);
  const invalidTagValuesMap = computeInvalidTagValues(tagSchema, effectiveTagValues);
  const hasInvalidTagValues = Object.keys(invalidTagValuesMap).length > 0;

  return {
    ...state,
    isEdit,
    selectedTopic,
    tagSchema,
    effectiveTagValues,
    missingRequiredTagKeys,
    invalidTagValuesMap,
    hasInvalidTagValues,
    setStep,
    selectTopic,
    setName,
    setScopeModeForField,
    toggleExpanded,
    toggleTagLeaf,
    setTagGroupLeaves,
    setTagFreeText,
    toggleChannel,
    setOverride,
    setContacts,
    setSaving,
    setErrorMessage,
    goToFieldForEdit,
    finishEditingField,
  };
}
